"use client";

import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import BlogCardImage from "../components/BlogCardImage";

interface FeaturedPost {
  title: string;
  description: string;
  category: string;
  author: string;
  date: string;
  slug: string;
  tags: string[];
  image?: string;
}

interface LatestPost {
  title: string;
  category: string;
  author: string;
  date: string;
  slug: string;
  tags: string[];
}

interface BlogListProps {
  featuredPosts: FeaturedPost[];
  latestPosts: LatestPost[];
}

export default function BlogList({ featuredPosts, latestPosts }: BlogListProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [visibleCount, setVisibleCount] = useState<number>(4);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Handle Escape to blur and clear
      if (e.key === 'Escape') {
        searchInputRef.current?.blur();
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter posts based on selected category and search query
  const filterPosts = <T extends { category: string; title: string; description?: string }>(posts: T[]) => {
    let filtered = selectedCategory === "all"
      ? posts
      : posts.filter(post => post.category === selectedCategory);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const filteredFeatured = filterPosts(featuredPosts);
  const filteredLatest = filterPosts(latestPosts);

  const visibleLatest = filteredLatest.slice(0, visibleCount);
  const hasMore = visibleCount < filteredLatest.length;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 4);
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header with Search and Filters */}
      <div className="mb-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="relative w-full md:w-96">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery || ""}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search blog posts..."
              className="w-full pl-10 pr-16 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 focus:border-transparent text-gray-900"
              style={{ color: '#111827' }}
            />
            <kbd className="absolute right-3 top-1/2 transform -translate-y-1/2 hidden sm:inline-block text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded font-mono">
              ⌘K
            </kbd>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === "all"
                  ? "bg-lime-200 text-gray-900 hover:bg-lime-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Posts
            </button>
            <button
              onClick={() => setSelectedCategory("Engineering")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === "Engineering"
                  ? "bg-lime-200 text-gray-900 hover:bg-lime-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Engineering
            </button>
            <button
              onClick={() => setSelectedCategory("Product")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === "Product"
                  ? "bg-lime-200 text-gray-900 hover:bg-lime-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Product
            </button>
          </div>
        </div>

        <h1 className="text-4xl font-bold text-gray-900">
          {selectedCategory === "all" ? "All Posts" : `${selectedCategory} Posts`}
        </h1>
      </div>

      {/* Featured Posts */}
      {filteredFeatured.length > 0 && (
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {filteredFeatured.map((post, index) => (
            <article key={index} className="group cursor-pointer">
              <Link href={`/blog/${post.slug}`}>
                {post.image ? (
                  <div className="rounded-lg aspect-video mb-4 overflow-hidden bg-[#111]">
                    <img src={post.image} alt={post.title} className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <BlogCardImage category={post.category} title={post.title} />
                )}
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-block px-3 py-1 bg-lime-200 text-gray-900 rounded text-xs font-medium">
                      {post.category}
                    </span>
                    {post.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 group-hover:text-gray-600 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 line-clamp-3">
                    {post.description}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <img
                        src="https://www.gravatar.com/avatar/93047f1d8e343ca3af1451616829ceb1?s=150"
                        alt={post.author}
                        className="w-6 h-6 rounded-full"
                      />
                      <span>{post.author}</span>
                    </div>
                    <span>•</span>
                    <time>{post.date}</time>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-3 gap-12">
        {/* Latest Posts - Left Column */}
        <div className="lg:col-span-2">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Latest</h2>
          <div className="space-y-8">
            {visibleLatest.map((post, index) => (
              <article key={index} className="group">
                <Link href={`/blog/${post.slug}`}>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                        {post.category}
                      </span>
                      {post.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="inline-block px-2 py-1 bg-gray-50 text-gray-500 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-600 transition-colors">
                      {post.title}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <img
                          src="https://www.gravatar.com/avatar/93047f1d8e343ca3af1451616829ceb1?s=150"
                          alt={post.author}
                          className="w-5 h-5 rounded-full"
                        />
                        <span>{post.author}</span>
                      </div>
                      <span>|</span>
                      <time>{post.date}</time>
                    </div>
                  </div>
                </Link>
                {index < visibleLatest.length - 1 && (
                  <div className="mt-8 border-b border-gray-200"></div>
                )}
              </article>
            ))}
          </div>

          {hasMore && (
            <button
              onClick={handleLoadMore}
              className="mt-12 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Load More
            </button>
          )}
        </div>

        {/* Right Sidebar - CTA */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-[#f7f3e8] rounded-2xl p-8 border border-[#e7e0cf]">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Start securing your code
            </h2>
            <p className="text-gray-700 mb-6">
              Install Code Pathfinder and eliminate false positives from your security workflow.
            </p>
            <Link
              href="/quickstart"
              className="inline-flex items-center px-5 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Get started
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
